import { useEffect, useMemo, useState } from 'react'
import { fmtDateTime, fieldLabel, prettyUser, sendJson, getJson, type DiscrepanciesPayload, type HistoryPayload, type HistoryRow, type Summary, type SyncStatus } from './api'
import { DataGrid, type Column } from './DataGrid'
import { Badge, MemberLink, MembershipLink, Placeholder, useJson } from './common'
import { DonorfyPullBlock, UnpairedView } from './pairing'
import { BatchesPanel, ResolveCell, type WriterStates } from './changes'

// The global Integrations tab: the Sync buttons (Ontrack live; Donorfy and GoCardless pulls
// next build), what was pulled and when, and the sub-tabs Discrepancies · Historical changes ·
// Change batches · Monthly downloads · Unpaired. PR A ships the first two live.

type View = 'discrepancies' | 'history' | 'batches' | 'downloads' | 'unpaired'

export function IntegrationsView({ summary, reloadSummary }: { summary: Summary | null; reloadSummary: () => void }) {
  const [view, setView] = useState<View>('discrepancies')
  const [reloadKey, setReloadKey] = useState(0)
  const pairs = (t: string, s: string) => summary?.pairings.find((p) => p.entity_type === t && p.system === s)?.n ?? 0
  return (
    <div className="mbr">
      <div className="mbr-section">
        <h3>Sources</h3>
        <div className="mbr-pairings">
          <div className="mbr-pairing mbr-pairing--paired">
            <h4>Ontrack <Badge kind="ok">Connected</Badge></h4>
            <SyncBlock onCompleted={() => { setReloadKey((k) => k + 1); reloadSummary() }} />
            <WriterSwitch system="ontrack" onChanged={() => { setReloadKey((k) => k + 1); reloadSummary() }} />
          </div>
          <div className="mbr-pairing mbr-pairing--paired">
            <h4>Donorfy <Badge kind="ok">Connected</Badge></h4>
            <DonorfyPullBlock onCompleted={() => { setReloadKey((k) => k + 1); reloadSummary() }} />
            <div className="mbr-note">Paired members: {pairs('member', 'donorfy').toLocaleString('en-GB')}. Nothing is written to Donorfy except by Write Now, behind the switch below.</div>
            <WriterSwitch system="donorfy" onChanged={() => { setReloadKey((k) => k + 1); reloadSummary() }} />
          </div>
          <div className="mbr-pairing mbr-pairing--paired">
            <h4>GoCardless <Badge kind="ok">Cached nightly</Badge></h4>
            <SyncBlock url="/api/membership/sync/gocardless" label="Pull from GoCardless now" lastLabel="Last GoCardless refresh"
                       title="Queue the GoCardless refresh (Sync Hub job 24, shared with the Foundation tab): customers, mandates, subscriptions and the last 90 days of payments. Once an hour."
                       blurb={<>Customers, mandates, subscriptions and payments are mirrored every night by the Foundation job; this button runs it now. Paired here from the ids Ontrack holds: {pairs('member', 'gocardless').toLocaleString('en-GB')} members and {pairs('membership', 'gocardless').toLocaleString('en-GB')} memberships. GoCardless is pull-only.</>}
                       onCompleted={() => { setReloadKey((k) => k + 1); reloadSummary() }} />
          </div>
        </div>
        {summary && (
          <div className="mbr-table-wrap" style={{ maxHeight: 'none', marginTop: 10 }}>
            <table className="hc-table mbr-meta-table">
              <thead><tr><th>What</th><th>Last refreshed (UTC)</th><th className="ta-right">Rows</th><th>Status</th><th>Note</th></tr></thead>
              <tbody>
                {summary.meta.map((m) => (
                  <tr key={m.source}><td>{metaLabel(m.source)}</td><td>{m.refreshed_at ? fmtDateTime(m.refreshed_at) : 'never'}</td><td className="ta-right">{m.row_count.toLocaleString('en-GB')}</td><td>{m.status}</td><td className="wrap">{m.message ?? ''}</td></tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className="mbr-pills" role="tablist">
        {([
          ['discrepancies', 'Discrepancies', summary?.counts.members_discrepant ?? null],
          ['history', 'Historical changes', summary?.counts.history ?? null],
          ['batches', 'Change batches', null],
          ['downloads', 'Monthly downloads', null],
          ['unpaired', 'Unpaired', null],
        ] as [View, string, number | null][]).map(([k, label, n]) => (
          <button key={k} type="button" role="tab" aria-selected={view === k} className={'mbr-pill mbr-pill--sub' + (view === k ? ' active' : '')} onClick={() => setView(k)}>
            {label}{n !== null && <span className="mbr-pill-n">{n.toLocaleString('en-GB')}</span>}
          </button>
        ))}
      </div>

      {view === 'discrepancies' && <DiscrepanciesTable key={`d-${reloadKey}`} />}
      {view === 'history' && <HistoryTable key={`h-${reloadKey}`} />}
      {view === 'batches' && <BatchesPanel key={`b-${reloadKey}`} />}
      {view === 'downloads' && <Placeholder title="Monthly downloads">one workbook per month — members and memberships as at the last pull, the month's payments and the month's history — arrives in a later build.</Placeholder>}
      {view === 'unpaired' && <UnpairedView key={`u-${reloadKey}`} onChanged={() => { setReloadKey((k) => k + 1); reloadSummary() }} />}
    </div>
  )
}

function metaLabel(source: string): string {
  switch (source) {
    case 'ontrack-pull': return 'Ontrack pull (all tables)'
    case 'ontrack-reconcile': return 'Reconcile into Membership records'
    case 'payments': return 'Unified payment list'
    case 'cache:OntrackMembership': return 'Ontrack memberships (raw copy)'
    case 'cache:OntrackMember': return 'Ontrack members (raw copy)'
    case 'cache:OntrackContact': return 'Ontrack contacts (raw copy)'
    case 'cache:OntrackPayment': return 'Ontrack payments (raw copy)'
    case 'cache:OntrackLookup': return 'Ontrack lookups (raw copy)'
    case 'ontrack-writer': return 'Ontrack writer switch'
    case 'ontrack-write': return 'Last write to Ontrack'
    case 'donorfy-writer': return 'Donorfy writer switch'
    case 'donorfy-writer-test': return 'Donorfy test constituent'
    case 'donorfy-write': return 'Last write to Donorfy'
    case 'donorfy-pull': return 'Donorfy pull (constituents)'
    case 'donorfy-reconcile': return 'Reconcile Donorfy pairings'
    default: return source
  }
}

// ---------------------------------------------------------------------------
//  The writer switches. Ontrack: off | on (OFF until the first supervised run). Donorfy: off | test | on —
//  test writes ONLY the one registered test constituent, created once from here, and locks Create in Donorfy.
//  While a switch is off, Write Now leaves that system's lines pending. Every flip is audited.
// ---------------------------------------------------------------------------
const SWITCH_CONFIRM: Record<string, string> = {
  'ontrack:on': 'Write Now will then send approved lines to Ontrack through the Sync Hub. Switch on?',
  'ontrack:off': 'Write Now will then leave Ontrack lines pending. Switch off?',
  'donorfy:test': 'Write Now will then write ONLY to the registered test constituent; every other Donorfy line stays pending, and nothing can be created. Switch to test?',
  'donorfy:on': 'Write Now will then write to ANY paired Donorfy constituent, and Create in Donorfy becomes available on unpaired members. Switch on?',
  'donorfy:off': 'Write Now will then leave Donorfy lines pending. Switch off?',
}
export function WriterSwitch({ system, onChanged }: { system: 'ontrack' | 'donorfy'; onChanged: () => void }) {
  const [states, setStates] = useState<WriterStates | null>(null)
  const [confirm, setConfirm] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [note, setNote] = useState<string | null>(null)
  const load = async () => { try { setStates(await getJson<WriterStates>('/api/membership/writer')); setError(null) } catch (e) { setError(String((e as Error)?.message ?? e)) } }
  useEffect(() => { void load() }, [])
  const flip = async (to: string) => {
    setBusy(true); setError(null)
    try { setStates(await sendJson<WriterStates>('/api/membership/writer', 'PUT', { system, status: to })); setConfirm(null); onChanged() }
    catch (e) { setError(String((e as Error)?.message ?? e)) } finally { setBusy(false) }
  }
  const registerTest = async () => {
    setBusy(true); setError(null)
    try {
      const r = await sendJson<{ test: { constituent_number: number; member_id: number }; writer: WriterStates }>('/api/membership/writer/donorfy-test', 'POST', {})
      setStates(r.writer); setConfirm(null); setNote(`Test constituent ${r.test.constituent_number} created in Donorfy and paired to the archived test member ${r.test.member_id}. Test mode writes to that record only.`); onChanged()
    } catch (e) { setError(String((e as Error)?.message ?? e)) } finally { setBusy(false) }
  }
  const s = states ? states[system] : null
  const label = system === 'ontrack' ? 'Ontrack writer' : 'Donorfy writer'
  const modes = system === 'ontrack' ? ['off', 'on'] : ['off', 'test', 'on']
  const test = system === 'donorfy' && states ? states.donorfy.test : null
  const kind = (st: string | undefined) => (st === 'on' ? 'ok' : st === 'test' ? 'warn' : 'muted')
  return (
    <div className="mbr">
      <div className="mbr-sync">
        <span>{label}: {s ? <Badge kind={kind(s.status)}>{s.status.toUpperCase()}</Badge> : <span className="mbr-note">…</span>}</span>
        {confirm === null && modes.filter((m) => m !== s?.status).map((m) => (
          <button key={m} type="button" className="btn-secondary" disabled={busy || !s} title={SWITCH_CONFIRM[`${system}:${m}`]} onClick={() => setConfirm(m)}>Switch to {m}</button>
        ))}
        {confirm === null && system === 'donorfy' && s?.status === 'test' && !test && (
          <button type="button" className="btn-secondary" disabled={busy} onClick={() => setConfirm('register')} title="Creates the one and only test constituent in Donorfy (Reports Writer Test) and an archived local test member paired to it.">Create the test constituent…</button>
        )}
        {confirm === 'register' && (
          <>
            <span className="mbr-note">This creates ONE constituent in Donorfy ("Reports Writer Test", ExternalKey REPORTS-WRITER-TEST, Pickering YO18 7AJ) and an archived local test member paired to it. In test mode only that record is ever written. Create it?</span>
            <button type="button" className="btn-primary" disabled={busy} onClick={() => void registerTest()}>{busy ? 'Creating…' : 'Yes, create the test constituent'}</button>
            <button type="button" className="btn-link" disabled={busy} onClick={() => setConfirm(null)}>Cancel</button>
          </>
        )}
        {confirm !== null && confirm !== 'register' && (
          <>
            <span className="mbr-note">{SWITCH_CONFIRM[`${system}:${confirm}`]}</span>
            <button type="button" className="btn-primary" disabled={busy} onClick={() => void flip(confirm)}>{busy ? 'Working…' : `Yes, switch to ${confirm}`}</button>
            <button type="button" className="btn-link" disabled={busy} onClick={() => setConfirm(null)}>Cancel</button>
          </>
        )}
        {error && <span className="mbr-error">{error}</span>}
      </div>
      {note && <div className="mbr-note">{note}</div>}
      <div className="mbr-note">
        {s?.message ?? ''}{s?.message ? '. ' : ''}
        {system === 'donorfy' && (test ? <>Test constituent: Donorfy <strong>{test.constituent_number}</strong> ↔ test member <MemberLink id={test.member_id}>{test.member_id}</MemberLink> (archived, hidden by default). </> : <>No test constituent registered yet. </>)}
        Last write run: <strong>{s?.last_run?.refreshed_at ? `${fmtDateTime(s.last_run.refreshed_at)} UTC` : 'never'}</strong>{s?.last_run?.message ? ` — ${s.last_run.message}` : ''}.{' '}
        {system === 'ontrack'
          ? 'Writes are contact and address fields of the member’s Ontrack contact only, one line at a time, lock-checked and read back; never a new record, never a membership.'
          : 'Writes are the shared contact fields of the paired constituent only, one constituent at a time, checked against Donorfy first and read back. Create in Donorfy works only when the switch is ON.'}
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
//  Sync from Ontrack — app.RefreshRequests kind 'membership' -> Sync Hub job 26
// ---------------------------------------------------------------------------
export function SyncBlock({ onCompleted, url = '/api/membership/sync/ontrack', label = 'Sync from Ontrack now', lastLabel = 'Last pull of Ontrack',
  title = 'Copy every Ontrack membership table into the reports database and reconcile it into the Membership records. About a minute. Not automatic: only this button runs it.',
  blurb = <>Pull from Ontrack: records Ontrack has that we do not are created here; fields only Ontrack holds mirror Ontrack; shared contact fields are never overwritten without a master.</> }:
  { onCompleted: () => void; url?: string; label?: string; lastLabel?: string; title?: string; blurb?: React.ReactNode }) {
  const [status, setStatus] = useState<SyncStatus | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [wasInFlight, setWasInFlight] = useState(false)
  const load = async () => {
    try {
      const j = await getJson<SyncStatus>(url)
      setStatus(j); setError(null)
      if (wasInFlight && !j.in_flight) { setWasInFlight(false); onCompleted() }
      if (j.in_flight) setWasInFlight(true)
    } catch (e) { setError(String((e as Error)?.message ?? e)) }
  }
  useEffect(() => {
    let alive = true
    const tick = async () => { if (alive) await load() }
    void tick()
    const id = setInterval(tick, status?.in_flight ? 6000 : 60000)
    return () => { alive = false; clearInterval(id) }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status?.in_flight, wasInFlight])
  const request = async () => {
    setBusy(true); setError(null)
    try { await sendJson(url, 'POST', {}); setWasInFlight(true); await load() }
    catch (e) { setError(String((e as Error)?.message ?? e)) } finally { setBusy(false) }
  }
  const s = status
  return (
    <div className="mbr">
      <div className="mbr-sync">
        <button type="button" className="btn-primary" disabled={busy || !s || !s.can_request} onClick={request} title={title}>
          {busy ? 'Requesting…' : s?.in_flight ? 'Refresh in progress…' : label}
        </button>
        <span className="mbr-status">
          {s?.in_flight && s.latest && <>Started by {prettyUser(s.latest.requested_by)} at {fmtDateTime(s.latest.requested_at)}{s.latest.message ? ` — ${s.latest.message}` : ''}. About a minute; this page reloads when it finishes. </>}
          {!s?.in_flight && s?.latest && <>Last sync requested by {prettyUser(s.latest.requested_by)} at {fmtDateTime(s.latest.requested_at)}{s.latest.status === 'completed' ? ' — ✓ successful' : s.latest.status === 'error' ? ` — ✗ failed${s.latest.message ? ` (${s.latest.message})` : ''}` : ` — ${s.latest.status}`}. </>}
          {s && !s.in_flight && (s.locked && s.next_available_at ? <>Available again at {fmtDateTime(s.next_available_at)}.</> : <>Available to run now.</>)}
          {error && <span className="mbr-error"> {error}</span>}
        </span>
      </div>
      <div className="mbr-note">
        {lastLabel}: <strong>{s?.last_refresh ? `${fmtDateTime(s.last_refresh)} UTC` : 'never'}</strong>{s?.last_refresh_message ? ` — ${s.last_refresh_message}` : ''}. {blurb}
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
//  Historical changes — one record's, or everything
// ---------------------------------------------------------------------------
export function HistoryTable({ entityType, entityId }: { entityType?: 'member' | 'membership'; entityId?: number }) {
  const [page, setPage] = useState(1)
  const [size, setSize] = useState(50)
  const [q, setQ] = useState('')
  const [draft, setDraft] = useState('')
  const [action, setAction] = useState('')
  useEffect(() => { const id = setTimeout(() => setQ(draft), 350); return () => clearTimeout(id) }, [draft])
  const url = useMemo(() => {
    const p = new URLSearchParams({ page: String(page), size: String(size) })
    if (entityType && entityId) { p.set('entity_type', entityType); p.set('entity_id', String(entityId)) }
    if (q) p.set('q', q)
    if (action) p.set('action', action)
    return `/api/membership/history?${p.toString()}`
  }, [page, size, q, action, entityType, entityId])
  const data = useJson<HistoryPayload>(url)
  const columns: Column<HistoryRow>[] = useMemo(() => [
    { key: 'at', label: 'When', sortable: false, render: (r) => fmtDateTime(r.at) },
    { key: 'who', label: 'Who', sortable: false, render: (r) => prettyUser(r.who) },
    ...(entityType ? [] : [{ key: 'record', label: 'Record', sortable: false, render: (r: HistoryRow) => r.entity_id ? (r.entity_type === 'member' ? <MemberLink id={r.entity_id}>{r.record_name ?? `member ${r.entity_id}`}{r.record_number ? ` (${r.record_number})` : ''}</MemberLink> : <MembershipLink id={r.entity_id}>{`membership ${r.record_number ?? r.entity_id}`}{r.record_name ? ` — ${r.record_name}` : ''}</MembershipLink>) : <span className="mbr-note">(all)</span> } as Column<HistoryRow>]),
    { key: 'system', label: 'Written to', sortable: false, render: (r) => systemLabel(r.system) },
    { key: 'action', label: 'Action', sortable: false, render: (r) => actionLabel(r.action) },
    { key: 'field', label: 'Field', sortable: false, render: (r) => (r.field ? fieldLabel(r.field) : '') },
    { key: 'old_value', label: 'Was', sortable: false, className: 'wrap', render: (r) => r.old_value ?? '' },
    { key: 'new_value', label: 'Became', sortable: false, className: 'wrap', render: (r) => r.new_value ?? '' },
    { key: 'detail', label: 'Detail', sortable: false, className: 'wrap', render: (r) => r.detail ?? '' },
  ], [entityType])
  return (
    <div className="mbr">
      <div className="mbr-toolbar">
        <label className="mbr-field mbr-field--search">Search history<input type="search" value={draft} onChange={(e) => { setDraft(e.target.value); setPage(1) }} placeholder="who, field, value, name" /></label>
        <label className="mbr-field">Action
          <select value={action} onChange={(e) => { setAction(e.target.value); setPage(1) }}>
            <option value="">Any</option>
            {['created', 'pulled', 'synced', 'updated', 'paired', 'unpaired', 'master_set', 'queued', 'approved', 'unapproved', 'written', 'verified', 'failed', 'requeued', 'removed', 'reverted', 'ignored', 'unignored', 'batch_written', 'writer_switched', 'deleted_in_ontrack', 'sync_requested'].map((a) => <option key={a} value={a}>{actionLabel(a)}</option>)}
          </select>
        </label>
      </div>
      <DataGrid<HistoryRow> columns={columns} rows={data.data?.rows ?? []} total={data.data?.total ?? 0} page={page} size={size} sort="at" dir="desc"
        loading={data.loading} error={data.error} rowKey={(r) => r.history_id} onSort={() => undefined} onPage={setPage} onSize={(s) => { setSize(s); setPage(1) }}
        sizes={[50, 100, 200]} emptyText="No history yet." />
    </div>
  )
}

const systemLabel = (s: string) => (s === 'ontrack' ? 'Ontrack' : s === 'donorfy' ? 'Donorfy' : s === 'gocardless' ? 'GoCardless' : 'Membership')
const actionLabel = (a: string) => ({
  created: 'Created', pulled: 'Pulled', updated: 'Updated', paired: 'Paired', unpaired: 'Unpaired', master_set: 'Master set', written: 'Written',
  verified: 'Verified', reverted: 'Reverted', deleted_in_ontrack: 'Deleted in Ontrack', sync_requested: 'Sync requested', ignored: 'Ignored', batch_written: 'Batch written',
  queued: 'Queued', approved: 'Approved for writing', unapproved: 'Back to pending', failed: 'Failed', requeued: 'Retried', removed: 'Removed', unignored: 'Unignored', writer_switched: 'Writer switched',
  synced: 'Synced (one record)',
} as Record<string, string>)[a] ?? a

// ---------------------------------------------------------------------------
//  Discrepancies — global list of every shared field that differs from Ontrack
// ---------------------------------------------------------------------------
interface DiscRow { member_id: number; full_name: string | null; membership_card_number: string | null; membership_id: number | null; membership_number: number | null; field: string; membership_value: string | null; ontrack_value: string | null; donorfy_value: string | null; differs_ontrack: boolean | number; differs_donorfy: boolean | number; ignored_ontrack: number; ignored_donorfy: number; master: string | null }

export function DiscrepanciesTable() {
  const [page, setPage] = useState(1)
  const [size, setSize] = useState(50)
  const [field, setField] = useState('')
  const [system, setSystem] = useState('')
  const [q, setQ] = useState('')
  const [draft, setDraft] = useState('')
  const [reloadKey, setReloadKey] = useState(0)
  useEffect(() => { const id = setTimeout(() => setQ(draft), 350); return () => clearTimeout(id) }, [draft])
  const url = useMemo(() => {
    const p = new URLSearchParams({ page: String(page), size: String(size) })
    if (field) p.set('field', field)
    if (system) p.set('system', system)
    if (q) p.set('q', q)
    p.set('k', String(reloadKey))
    return `/api/membership/discrepancies?${p.toString()}`
  }, [page, size, field, system, q, reloadKey])
  const data = useJson<DiscrepanciesPayload>(url)
  const columns: Column<DiscRow>[] = useMemo(() => [
    { key: 'full_name', label: 'Member', sortable: false, render: (r) => <MemberLink id={r.member_id}>{r.full_name ?? `member ${r.member_id}`}</MemberLink> },
    { key: 'membership_card_number', label: 'Card no', sortable: false },
    { key: 'membership_number', label: 'Membership no', sortable: false, render: (r) => <MembershipLink id={r.membership_id}>{r.membership_number ?? ''}</MembershipLink> },
    { key: 'field', label: 'Field', sortable: false, render: (r) => fieldLabel(r.field) },
    { key: 'membership_value', label: 'Membership (this system)', sortable: false, className: 'wrap mbr-diff-ours', render: (r) => r.membership_value ?? <span className="mbr-empty-val">(blank)</span> },
    { key: 'ontrack_value', label: 'Ontrack', sortable: false, className: 'wrap', render: (r) => <span className={r.differs_ontrack ? 'mbr-diff-cell' : undefined}>{r.ontrack_value ?? <span className="mbr-empty-val">(blank)</span>}{r.ignored_ontrack ? <> <Badge kind="muted">ignored</Badge></> : null}</span> },
    { key: 'donorfy_value', label: 'Donorfy', sortable: false, className: 'wrap', render: (r) => <span className={r.differs_donorfy ? 'mbr-diff-cell' : undefined}>{r.donorfy_value ?? <span className="mbr-empty-val">{r.differs_donorfy ? '(blank)' : '—'}</span>}{r.ignored_donorfy ? <> <Badge kind="muted">ignored</Badge></> : null}</span> },
    { key: 'master', label: 'Master', sortable: false, render: (r) => r.master ?? 'none' },
    { key: 'resolve', label: 'Resolve', sortable: false, className: 'wrap', render: (r) => <ResolveCell row={r} scope="global" compact onDone={() => setReloadKey((k) => k + 1)} /> },
  ], [])
  return (
    <div className="mbr">
      <p className="mbr-blurb">Every shared field where this system, Ontrack and the paired Donorfy record disagree, as at the last pull. Pick the correct value and Apply: the Membership record changes now (audited) and the value is queued for every paired system that differs, in the global change batch, ready for Write Now once that system's writer exists. Ignore leaves a difference alone and stops counting it.</p>
      <div className="mbr-toolbar">
        <label className="mbr-field mbr-field--search">Search<input type="search" value={draft} onChange={(e) => { setDraft(e.target.value); setPage(1) }} placeholder="name, card no, value" /></label>
        <label className="mbr-field">Field
          <select value={field} onChange={(e) => { setField(e.target.value); setPage(1) }}>
            <option value="">Any</option>
            {(data.data?.fields ?? []).map((f) => <option key={f.field} value={f.field}>{fieldLabel(f.field)} ({f.n})</option>)}
          </select>
        </label>
        <label className="mbr-field">Differs from
          <select value={system} onChange={(e) => { setSystem(e.target.value); setPage(1) }}>
            <option value="">Either</option>
            <option value="ontrack">Ontrack{data.data?.by_system ? ` (${data.data.by_system.ontrack})` : ''}</option>
            <option value="donorfy">Donorfy{data.data?.by_system ? ` (${data.data.by_system.donorfy})` : ''}</option>
          </select>
        </label>
      </div>
      <DataGrid<DiscRow> columns={columns} rows={data.data?.rows ?? []} total={data.data?.total ?? 0} page={page} size={size} sort="full_name" dir="asc"
        loading={data.loading} error={data.error} rowKey={(r) => `${r.member_id}-${r.field}`} onSort={() => undefined} onPage={setPage} onSize={(s) => { setSize(s); setPage(1) }}
        sizes={[50, 100, 200]} emptyText="No discrepancies with Ontrack." />
    </div>
  )
}
